import type { SimulatorReservation, SimulatorReservationPayload } from "Models";
import {
  getSimulatorReservationsByFacilityId,
  makeCreateSimulatorReservationMutation,
  makeUpdateSimulatorReservationMutation,
} from "modules/simulator-reservations/simulatorReservationQueries";
import type { UseDataInfo } from "QueryTypes";
import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import type { QueryState, EntitiesState } from "redux-query";
import { useMutation, useRequest, type RunMutation } from "redux-query-react";
import { useMemo } from "react";
import type { RootState } from "StoreTypes";
import { simulatorReservationIsWithinTimeRange } from "./simulatorReservationHelpers";
import {
  makeDeleteSimulatorReservationMutation,
  makeSendSimulatorReservationToSimMutation,
} from "./simulatorReservationQueries";
import type { SendRoundRangeToSimInfo } from "Messages";

export const useCreateSimulatorReservation = (
  facilityId: string,
): [QueryState, RunMutation<EntitiesState, [SimulatorReservationPayload]>] => {
  const [createSimulatorReservationQueryState, createSimulatorReservation] =
    useMutation((body: SimulatorReservationPayload) =>
      makeCreateSimulatorReservationMutation(facilityId, body),
    );

  return [createSimulatorReservationQueryState, createSimulatorReservation];
};

// Selector factory to create memoized selectors for different facility/time combinations
const createSimulatorReservationsSelector = (
  facilityId: string,
  startTime?: string,
  endTime?: string,
) =>
  createSelector(
    [
      (state: RootState) =>
        state.entities.simulatorReservations?.byFacilityId?.[facilityId],
    ],
    (reservations) => {
      if (!reservations) return [];

      let filteredReservations =
        Object.values<SimulatorReservation>(reservations);

      if (startTime && endTime) {
        filteredReservations = filteredReservations.filter((reservation) =>
          simulatorReservationIsWithinTimeRange(
            reservation,
            startTime,
            endTime,
          ),
        );
      }

      return filteredReservations.sort(
        (a: SimulatorReservation, b: SimulatorReservation) =>
          new Date(a.startTime).valueOf() - new Date(b.startTime).valueOf(),
      );
    },
  );

export const useSimulatorReservationsByFacilityId = (
  facilityId: string,
  startTime?: string,
  endTime?: string,
): UseDataInfo<SimulatorReservation[]> => {
  const safeQuery = useMemo(() => {
    if (!facilityId?.trim()) {
      console.log(
        "facilityId is falsy or empty for simulator reservations query",
      );
      return null;
    }
    return getSimulatorReservationsByFacilityId(facilityId, startTime, endTime);
  }, [facilityId, startTime, endTime]);

  const [queryState, refreshSimulatorReservations] = useRequest(
    safeQuery ?? undefined,
  );

  const memoizedSelector = useMemo(
    () => createSimulatorReservationsSelector(facilityId, startTime, endTime),
    [facilityId, startTime, endTime],
  );

  const simulatorReservations = useSelector(memoizedSelector);

  return [simulatorReservations, queryState, refreshSimulatorReservations];
};

export const useDeleteSimulatorReservations = (
  reservationId: string,
  facilityId: string,
) => {
  // [QueryState, RunMutation<EntitiesState>]
  //const [queryState, deleteSimulatorReservation] =
  return useMutation(() =>
    makeDeleteSimulatorReservationMutation(reservationId, facilityId),
  );
};

export const useUpdateSimulatorReservation = (
  reservationId: string,
  facilityId: string,
): [QueryState, RunMutation<EntitiesState, [SimulatorReservationPayload]>] => {
  const [queryState, updateSimulatorReservation] = useMutation(
    (body: SimulatorReservationPayload) =>
      makeUpdateSimulatorReservationMutation(reservationId, facilityId, body),
  );

  return [queryState, updateSimulatorReservation];
};

export const useSendSimulatorReservationToSim = (
  simulatorReservationId: string,
): [QueryState, RunMutation<EntitiesState, [string]>] => {
  const [senddToSimulatorQueryState, sendSimulatorReservation] = useMutation(
    () => makeSendSimulatorReservationToSimMutation(simulatorReservationId),
  );

  return [senddToSimulatorQueryState, sendSimulatorReservation];
};

export const useSelectedSimulatorReservation = () => {
  // todo: this doesn't seem correct
  return useSelector((state: RootState): SendRoundRangeToSimInfo | undefined => {
    const selectedSimulatorReservation =
      state.simulatorReservation?.selectedSimulatorReservation;
    return Array.isArray(selectedSimulatorReservation) ? undefined : selectedSimulatorReservation;
  });
};

export const useSelectedQueueItem = (facilityId: string) => {
  return useSelector((state: RootState): SimulatorReservation => {
    const selectedQueueItemId = state.queue?.selectedQueueItem;

    return state.entities?.simulatorReservations?.byFacilityId?.[facilityId]?.[
      selectedQueueItemId
    ];
  });
};

// Selector factory for simulator reservations by facility and simulator ID
const createSimulatorReservationsByFacilityAndSimSelector = (
  facilityId: string,
  simulatorId: string,
  startTime?: string,
  endTime?: string,
) =>
  createSelector(
    [
      (state: RootState) =>
        state.entities.simulatorReservations?.byFacilityId?.[facilityId],
    ],
    (reservations) => {
      if (!reservations) return [];

      let filteredReservations =
        Object.values<SimulatorReservation>(reservations);

      if (startTime && endTime) {
        filteredReservations = filteredReservations.filter((reservation) =>
          simulatorReservationIsWithinTimeRange(
            reservation,
            startTime,
            endTime,
          ),
        );
      }

      return filteredReservations.filter(
        (res) => res?.simulatorId === simulatorId,
      );
    },
  );

export const useSimulatorReservationsByFacilityAndSimId = (
  facilityId: string,
  simulatorId: string,
  startTime?: string,
  endTime?: string,
): SimulatorReservation[] => {
  const memoizedSelector = useMemo(
    () =>
      createSimulatorReservationsByFacilityAndSimSelector(
        facilityId,
        simulatorId,
        startTime,
        endTime,
      ),
    [facilityId, simulatorId, startTime, endTime],
  );

  const simulatorReservations = useSelector(memoizedSelector);

  return simulatorReservations;
};
